import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignContactModel, CampaignContactRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignContacts']

type CampaignContactFilters = {
  campaign_id?: number,
}

type UseCampaignContactsOptions = {
  filters?: CampaignContactFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignContactRequestOptions,
}

function useCampaignContacts(options: UseCampaignContactsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
  } = filters

  const {
    updatedEntities: { campaignContacts: campaignContactsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignContacts } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignContacts = useMemo(() => {
    const filtered = Object.values(campaignContacts).filter((campaignContact: CampaignContactModel) => {
      const { campaign_id } = campaignContact

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)

      return matchCampaignId
    })

    return sortArrayBy(filtered, 'asc')
  }, [campaignContactsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignContactsCount = filteredCampaignContacts.length
  const hasFilteredCampaignContacts = !!filteredCampaignContactsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignContactsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignContacts } = useReduxAction(
    'campaignContacts',
    'loadCampaignContacts',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignContacts,
    filteredCampaignContactsCount,
    hasFilteredCampaignContacts,
    loading: loadingCampaignContacts,
  }
}

export type UseCampaignContactsPayload = ReturnType<typeof useCampaignContacts>

export default useCampaignContacts
